<template>
  <div class="row">
    <div class="col-md-12">
      <div class="row">
        <div class="col-md-6">
          <h4>Payment Authorized</h4>
        </div>
        <div class="col-md-3">
          <h4 class="text-center">Order Amount</h4>
        </div>
        <div class="col-md-3">
          <h4 class="text-center">Refund Amount</h4>
        </div>
      </div>
      <div class="row">
        <div class="col-md-3">
          <strong>Processing Method</strong>
        </div>
      </div>
      <div class="row">
        <div class="col-md-3">
          <strong>Payment Methods</strong>
        </div>
        <div class="col-md-3">
          <template v-for="paymentGateway in order.payment_gateway_names">
            <div>{{paymentGateway | underscoresToSpaces | capitalize}}</div>
          </template>
        </div>
      </div>
      <div class="row">
        <div class="col-md-3">
          <strong>Subtotal</strong>
        </div>
        <div class="col-md-3 col-md-offset-3">
          <div class="text-center">
            <price-display
                :originalPrice="Number(order.total_line_items_price)"
                :updatePrice="Number(order.subtotal_price)"
            />
          </div>
        </div>
        <div class="col-md-3">
          <div class="text-center"
               :class="[$v.refundSubtotalPrice.$invalid ? 'text-danger' : 'text-success']">
            {{draftRA.subtotal_price | currency}}
            <p v-if="!$v.refundSubtotalPrice.minValue">
              Refund subtotal amount doesn't meet minimum threshold
              ({{minimumRefundSubtotal | currency}}).
            </p>
            <p v-if="!$v.refundSubtotalPrice.maxValue">
              Refund subtotal amount larger than original order amount.
            </p>
          </div>
        </div>
      </div>
      <br>
      <div class="row" v-if="order.shipping_lines.length > 0">
        <div class="col-md-3">
          <strong>Shipping</strong>
        </div>
        <div class="col-md-3">{{order.shipping_lines[0].title}}</div>
        <div class="col-md-3">
          <div class="text-center">
            <price-display
                :originalPrice="Number(order.shipping_lines[0].price)"
                :updatePrice="Number(order.shipping_lines[0].discounted_price)"
            />
          </div>
        </div>
        <div class="col-md-3">
          <div class="text-center">
            <input-masked-price :price="shippingLine.price"
                                @input="(value) => updateShippingLine(value)"
                                :class="{'el-input-border--danger': $v.shipping.$invalid}"
                                :disabled="isLoading"/>
          </div>
        </div>
      </div>
      <br>
      <div class="row" v-if="discount.price_rule">
        <div class="col-md-3"><strong>Discount</strong></div>
        <div class="col-md-9">
          <template v-if="discount.price_rule.value_type === 'fixed_amount'">
            {{discount.price_rule.value | currency}}
          </template>
          <template v-if="discount.price_rule.value_type === 'percentage'">
            {{discount.price_rule.value / 100 | percent}}
          </template>
          <i @click="removeDiscount" class="el-icon-delete text-danger"> -
            Remove Discount
          </i>
        </div>
      </div>
      <br>
      <div class="row m-b-1">
        <div class="col-md-3 col-md-offset-6">
          <div class="row" v-for="taxLine in orderTaxLines">
            <div class="col-md-8">
              {{taxLine.rate | percent}} {{taxLine.title}}
            </div>
            <div class="col-md-4">
              <div class="text-center">
                {{taxLine.price | currency}}
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3 b-l-1">
          <div class="row" v-for="taxLine in refundTaxLines">
            <div class="col-md-8">
              {{taxLine.rate | percent}} {{taxLine.title}}
            </div>
            <div class="col-md-4">
              <div class="text-center">
                {{taxLine.price | currency}}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-3">
          <strong>Tax Total</strong>
        </div>
        <div class="col-md-3">&nbsp;</div>
        <div class="col-md-3">
          <div class="text-center">
            {{order.total_tax | currency}}
          </div>
        </div>
        <div class="col-md-3">
          <div class="text-center">
            {{draftRA.total_tax | currency}}
          </div>
        </div>
      </div>
      <template v-if="order && order.adjustments.length > 0">
      <div class="row">
        <div class="col-md-3">
          <strong>Adjustments</strong>
        </div>
        <div class="col-md-3">&nbsp;</div>
        <div class="col-md-3">
          <div class="text-center">
            <div class="row" v-for="adj in order.adjustments">
              <div class="col-md-12">
                -{{adj.amount | currency}}
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3">
        </div>
      </div>
      </template>
      <hr>
      <div class="row">
        <div class="col-md-6">
          <strong>Total</strong>
        </div>
        <div class="col-md-3">
          <div class="text-center">
            {{originalTotalPrice | currency}}
          </div>
        </div>
        <div class="col-md-3">
          <div class="text-center"
               :class="[$v.refundTotalPrice.$invalid ? 'text-danger' : 'text-success']">
            <strong>{{draftRA.total_price | currency}}</strong>
            <p v-if="!$v.refundTotalPrice.minValue">
              Refund total amount doesn't meet minimum threshold of
              {{minimumRefundTotal | currency}}.
            </p>
            <p v-if="!$v.refundTotalPrice.maxValue">
              Refund total amount larger than original order amount.
            </p>
            <p v-if="$v.refundTotalPrice.maxValue && originalTotalPrice < draftRA.total_price">
              Total within rounding error.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {mapState, mapMutations} from 'vuex';
  import {
    currency,
    percent,
    underscoresToSpaces,
    capitalize,
  } from '../../../filters';
  import {rollUpTaxLines} from '../../../utils';
  import InputMaskedPrice from '../Shared/InputMaskedPrice';
  import PriceDisplay from '../Shared/PriceDisplay';
  import {
    ORDER_RETURN_SET_SHIPPING,
    ORDER_RETURN_SET_DISCOUNT,
  } from '../../../store/modules/order/return/types';
  import {
    ORDER_RETURN_MINIMUM_SUBTOTAL_REFUND_AMOUNT,
    ORDER_RETURN_MINIMUM_TOTAL_REFUND_AMOUNT,
  } from '../../../lang/en/constants';

  export default {
    name: 'OrderReturnPayment',
    inject: ['$v'],
    components: {
      InputMaskedPrice,
      PriceDisplay,
    },
    filters: {
      currency,
      percent,
      underscoresToSpaces,
      capitalize,
    },
    computed: {
      ...mapState({
        isLoading: state => state.orderReturn.loading,
        order: state => state.orderReturn.order,
        draftRA: state => state.orderReturn.draftRA,
        discount: state => state.orderReturn.discount,
        shippingLine: state => state.orderReturn.shipping,
        orderTaxLines: state => {
          return rollUpTaxLines(state.orderReturn.order.tax_lines);
        },
        refundTaxLines: state => {
          return rollUpTaxLines(state.orderReturn.draftRA.tax_lines);
        },
        originalTotalPrice: state => (state.orderReturn.order
            ? parseFloat(state.orderReturn.order.total_price) - state.orderReturn.order.adjustments.reduce((carry, current) => {
              return carry + parseFloat(current.amount);
            }, 0)
            : 0)
      }),
    },
    methods: {
      ...mapMutations({
        updateShipping: ORDER_RETURN_SET_SHIPPING,
        setDiscount: ORDER_RETURN_SET_DISCOUNT,
      }),
      updateShippingLine(value) {
        const shipping = {...this.shippingLine, ...{price: Number(value)}};
        this.updateShipping(shipping);
        this.$v.shipping.price.$touch();
      },
      removeDiscount() {
        this.$v.$touch();
        this.setDiscount({...this.discount, ...{price_rule: null}});
      },
      currency,
    },
    data() {
      return {
        minimumRefundTotal: ORDER_RETURN_MINIMUM_TOTAL_REFUND_AMOUNT,
        minimumRefundSubtotal: ORDER_RETURN_MINIMUM_SUBTOTAL_REFUND_AMOUNT,
      };
    },
  };
</script>
